import type {
  HomeDto,
  Resident,
  ResidentBody,
  ResidentCarePackageDTO
} from '@lmc/ts-common'
import {
  Add,
  Button,
  Form,
  FormCard,
  FormItem,
  PageHeader,
  TextArea
} from '@lmc/ui-kit'
import { Form as AntdForm } from 'antd'
import type { DefaultOptionType } from 'antd/es/select'
import { getNominative } from 'common/utils'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useHomeType from 'hooks/useHomeType'
import useResident from 'hooks/useResident'
import { useRouter } from 'hooks/useRouter'
import { type LegacyRef, type RefObject, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { scrollToSection } from 'screens/Residents/screens/ResidentProfileScreen/utils/utils'
import { commonPatterns } from 'utils'
import DeleteFundingTypeModal from '../ChangeManagement/DeleteFundingTypeModal'
import { ButtonsContainer, PageWrapper, SectionHeader } from '../components'
import { handleOnFinishFailed } from '../formComponents/utils'
import useCheckFundingType from '../hooks/useCheckFundingType'
import { FundingFormItem } from './components/FundingFormItem'

export const EditCarePackageAndPatternsScreen = () => {
  const { t } = useTranslation()
  const [form] = AntdForm.useForm()
  const { navigate, params } = useRouter()
  const [isDirty, setIsDirty] = useState(false)
  const { resident, updateResident, isLoadingUpdate } = useResident({
    id: params?.id,
    onUpdateSuccess: () => navigate(-1)
  })
  const flags = useFeatureFlags({
    flagNames: ['rostering_change_management_part_2']
  })?.data
  const { hasRota } = useHomeType()

  const ref = useRef()

  const flagsRosteringChangeManagementPart2Enabled =
    flags?.rostering_change_management_part_2?.enabled

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )

  const residentNominative = getNominative(
    home.preferredPeopleNominative,
    'Resident'
  )

  const fundingTypeOptions: DefaultOptionType[] =
    home.rotaSettings?.fundingTypes?.map((fundingType) => ({
      label: fundingType.name,
      value: fundingType.id
    })) || []

  const residentWithPackages = resident as unknown as Resident

  const [showDeleteFundingTypeModal, setShowDeleteFundingTypeModal] =
    useState(false)

  const { checkFundingTypeUsedInRota } = useCheckFundingType()

  const handleFormValuesChange = (values: ResidentCarePackageDTO) => {
    if (values) {
      setIsDirty(true)
    }
  }

  const handleOnUpdateFinish = (values: ResidentCarePackageDTO) => {
    setIsDirty(false)
    updateResident({
      carePackage: {
        ...values,
        fundingSources: values?.fundingSources?.map((source) => ({
          ...source
        }))
      }
    } as unknown as ResidentBody)
  }

  const initialValues: ResidentCarePackageDTO =
    residentWithPackages?.carePackage || {}

  // Test ID constants following best practices
  const baseTestId = 'care-package-edit'
  const cancelButtonTestId = commonPatterns.button(baseTestId, 'cancel')
  const saveButtonTestId = commonPatterns.button(baseTestId, 'save')
  const addFundingButtonTestId = commonPatterns.button(
    baseTestId,
    'add-funding'
  )
  const additionalNotesInputTestId = commonPatterns.input(
    baseTestId,
    'additional-notes'
  )
  const visitNotesInputTestId = commonPatterns.input(baseTestId, 'visit-notes')
  const mainSectionTestId = `${baseTestId}-main-section`
  const fundingFormCardTestId = `${baseTestId}-funding-form-card`
  const additionalNotesFormCardTestId = `${baseTestId}-additional-notes-form-card`
  const visitPreferencesFormCardTestId = `${baseTestId}-visit-preferences-form-card`
  const otherSectionTestId = `${baseTestId}-other-section`
  const navigationModalTestId = `${baseTestId}-navigation-modal`
  const deleteFundingModalTestId = `${baseTestId}-delete-funding-modal`

  return (
    <>
      <PageHeader
        title={`${t('EDIT')} ${t('CARE_PACKAGE_AND_PATTERNS')}`}
        sticky
      >
        <ButtonsContainer>
          <Button onClick={() => navigate(-1)} data-testid={cancelButtonTestId}>
            {t('CANCEL')}
          </Button>
          <Button
            variant="primary"
            onClick={form.submit}
            isLoading={isLoadingUpdate}
            data-testid={saveButtonTestId}
          >
            {t('SAVE')}
          </Button>
        </ButtonsContainer>
      </PageHeader>
      <PageWrapper>
        <SectionHeader
          title={t('CARE_PACKAGE_AND_PATTERNS')}
          testId={mainSectionTestId}
        />
        <Form
          form={form}
          onFinish={handleOnUpdateFinish}
          initialValues={initialValues}
          onFinishFailed={({ errorFields }) =>
            handleOnFinishFailed(errorFields)
          }
          onValuesChange={handleFormValuesChange}
          data-testid={`${baseTestId}-form`}
        >
          <AntdForm.List name="fundingSources">
            {(fields, { add, remove }) => (
              <FormCard
                title={t('FUNDING')}
                rightChildren={
                  <Button
                    variant="text"
                    type="button"
                    onClick={() => {
                      add()
                      scrollToSection(ref as unknown as RefObject<HTMLElement>)
                    }}
                    data-testid={addFundingButtonTestId}
                  >
                    <Add />
                    {t('ADD_FUNDING')}
                  </Button>
                }
                testId={fundingFormCardTestId}
              >
                {fields.map((field, index) => {
                  const fundingTypeId = form.getFieldValue([
                    'fundingSources',
                    field.name,
                    'type'
                  ])

                  const handleDelete = async () => {
                    if (hasRota && flagsRosteringChangeManagementPart2Enabled) {
                      const addressUsed = await checkFundingTypeUsedInRota({
                        fundingTypeId,
                        residentId: resident?.id || ''
                      })

                      const areAllArraysEmpty =
                        addressUsed?.instances?.length === 0 &&
                        addressUsed?.patterns?.length === 0 &&
                        addressUsed?.revisions?.length === 0

                      if (areAllArraysEmpty) remove(index)
                      else setShowDeleteFundingTypeModal(true)
                    } else remove(index)
                  }

                  return (
                    <div ref={ref as unknown as LegacyRef<HTMLDivElement>}>
                      <FundingFormItem
                        key={field.name}
                        onDelete={handleDelete}
                        name={field?.name}
                        fundingTypeOptions={fundingTypeOptions}
                        selectedResident={resident?.id}
                      />
                    </div>
                  )
                })}
              </FormCard>
            )}
          </AntdForm.List>
          <FormCard
            title={t('ADDITIONAL_NOTES')}
            testId={additionalNotesFormCardTestId}
          >
            <FormItem name="additionalNotes">
              <TextArea
                autoSize={{
                  minRows: 3
                }}
                maxLength={200}
                showCount
                data-testid={additionalNotesInputTestId}
              />
            </FormItem>
          </FormCard>
          {hasRota && (
            <>
              <SectionHeader title={t('OTHER')} testId={otherSectionTestId} />
              <FormCard
                title={t('VISIT_PREFERENCES')}
                subtitle={t('VISIT_PREFERENCES_DESC', {
                  nominative: residentNominative
                })}
                testId={visitPreferencesFormCardTestId}
              >
                <FormItem name="visitNotes">
                  <TextArea
                    autoSize={{
                      minRows: 3
                    }}
                    maxLength={200}
                    showCount
                    data-testid={visitNotesInputTestId}
                  />
                </FormItem>
              </FormCard>
            </>
          )}
        </Form>
      </PageWrapper>
      <NavigationModal
        canNavigate={!isDirty}
        modalContentText={t('CARE_PACKAGE_NAVIGATION_MODAL_CONTENT')}
        data-testid={navigationModalTestId}
      />
      {showDeleteFundingTypeModal && (
        <DeleteFundingTypeModal
          showModal={showDeleteFundingTypeModal}
          setShowModal={setShowDeleteFundingTypeModal}
          data-testid={deleteFundingModalTestId}
        />
      )}
    </>
  )
}

export default EditCarePackageAndPatternsScreen
